/**
 * STARSEED-CAPMARKETS-134 — pure logic behind the admin partner-account queue.
 *
 * This module exists so the two rules that matter most on that page are
 * testable without a DOM, and so one of them is enforced by a function
 * SIGNATURE rather than by reviewer vigilance:
 *
 *   1. No similarity matching, ever (114 AC4 / 134 AC5). `orderMembersForPicker`
 *      takes the member list and NOTHING ELSE — no account label, no ref. It
 *      is therefore incapable of ranking members by resemblance to the account
 *      being attached, because it never learns what that account is called.
 *      A future "helpful" sort would have to add a parameter, which is a
 *      visible change in a diff rather than a quiet reordering.
 *
 *   2. An absent figure is not zero (109 / 134 AC6). `describeMoney` returns
 *      an explicit unknown marker rather than a formatted `$0.00`. Note that
 *      `formatCurrency` in lib/api.js does `amount || 0`, so it renders null,
 *      undefined AND NaN as "$0.00" — it must NOT be used for queue figures,
 *      where a blank statement column is routine.
 */
import { toFiniteNumber } from "./heroBalance";

/**
 * Money for an admin table cell.
 *
 * Returns `{ known: false }` when the figure is absent — the caller renders
 * "Unknown", never a currency string. A real 0 is `{ known: true, text: "$0.00" }`,
 * because an account genuinely holding nothing is a different fact from an
 * account whose statement column was blank, and an admin deciding whether to
 * attach it needs to tell them apart.
 */
export const describeMoney = (value) => {
  const n = toFiniteNumber(value);
  if (n === null) return { known: false, text: null };
  return {
    known: true,
    text: new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(n),
  };
};

/** Whole days between two instants, floored, never negative. */
export const daysBetween = (fromIso, nowMs) => {
  if (!fromIso) return null;
  const then = new Date(fromIso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((nowMs - then) / 86400000);
  return days < 0 ? 0 : days;
};

/**
 * How long an account has been unattached.
 *
 * **Measured from the statement date, not from ingest.** The unresolved
 * projection carries `first_seen_as_of` (the earliest statement date among the
 * account's snapshots) and no ingestion timestamp, so this is the honest
 * quantity available: "this money has been unattached since the statement said
 * it existed". The label says `since <date>` rather than implying we know when
 * the row was created.
 */
export const describeWait = (account, nowMs) => {
  const since = account?.first_seen_as_of || null;
  const days = daysBetween(since, nowMs);
  if (days === null) return { known: false, days: null, label: "Unknown" };
  const unit = days === 1 ? "day" : "days";
  return { known: true, days, label: `${days} ${unit}` };
};

/**
 * Order members for the attach picker.
 *
 * **Deliberately takes only the member list.** See the module docblock: this
 * signature is 134 AC5's guarantee. Alphabetical by display name, with a
 * stable id tiebreak so two members sharing a name do not swap positions
 * between renders.
 */
export const orderMembersForPicker = (members) => {
  const rows = Array.isArray(members) ? members.filter(Boolean) : [];
  return [...rows].sort((a, b) => {
    const an = (a.full_name || a.email || "").toLowerCase();
    const bn = (b.full_name || b.email || "").toLowerCase();
    if (an < bn) return -1;
    if (an > bn) return 1;
    return String(a.id || "").localeCompare(String(b.id || ""));
  });
};

/**
 * Turn a 409 body from `POST /admin/partner-accounts/mappings` into something
 * renderable, without flattening it into a string.
 *
 * The server's shape (routes/partner_account_mappings.py) is
 * `{ detail: { message, conflicts: [{ partner_account_ref, mapped_member_id,
 * cached_member_id, source, detail }] } }`. Both member ids must survive to the
 * screen — 114 AC6's whole argument is that the server refuses to pick a winner,
 * and a UI that shows only "conflict!" would re-hide the thing it refused to
 * decide.
 */
export const parseConflictResponse = (error) => {
  // Status-gated on purpose (Copilot, PR #2401). Shape alone is not enough:
  // only 409 means "the server detected a disagreement and refused to pick a
  // winner". A future 400 or 500 that happens to carry a `conflicts` key would
  // otherwise render as a conflict panel offering `force`, which would invite
  // an operator to override something that was never a conflict.
  const status = error?.response?.status;
  if (status !== 409) return null;
  const detail = error?.response?.data?.detail;
  if (!detail || typeof detail !== "object") return null;
  const conflicts = Array.isArray(detail.conflicts) ? detail.conflicts : [];
  if (!conflicts.length) return null;
  return { message: detail.message || "Conflicting attachment", conflicts };
};

/** Display name for a member id, falling back to the id itself. */
export const memberLabel = (members, id) => {
  if (!id) return "unknown member";
  const hit = (members || []).find((m) => m && m.id === id);
  if (!hit) return id;
  return hit.full_name || hit.email || id;
};
